import { useNavigate } from '@tanstack/react-router';
import { useCallback, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  DateField,
  Kanban,
  KanbanCardBody,
  KanbanTag,
  Modal,
  ModalActions,
  Page,
  PageHeader,
  Select,
  Toolbar,
  type KanbanColor,
  type KanbanPage,
  type KanbanStage,
} from '@evogard/ui';
import {
  RETENTION_PHASE_LABELS,
  format,
  formatAge,
  fromColumn,
  parseRetentionColumnKey,
  retentionPhaseAgeTone,
} from '@evogard/domain';
import type { RetentionCard, RetentionSummary } from '@evogard/contract';
import { api, ok } from '../../lib/api.js';
import {
  useInvalidateRetention,
  useRetentionSummaries,
  type RetentionFilters,
  type RetentionTenant,
} from './useRetention.js';

/** Um cartão com a base de onde veio — o quadro junta as duas. */
interface BoardCard {
  card: RetentionCard;
  tenant: RetentionTenant;
}

/** O valor do banco como dinheiro na tela — vazio vira travessão. */
function moneyText(column: string | null): string {
  const m = column === null ? null : fromColumn(column);
  return m === null ? '—' : format(m);
}

// @valor
const TONE_OF_AGE = { verde: 'sucesso', amarelo: 'alerta', vermelho: 'erro' } as const;

/**
 * O Kanban Cancelamento — o quadro do `esteira-cancelamento`, com CE e GO
 * juntas.
 *
 * As colunas são as do legado: Início; para cada operador da escala, "Nome" e
 * "Nome · 48h"; o "72 horas - Envio de e-mail", de todos; e Retidos,
 * Cancelados e Divergência. Ninguém arrasta cartão: o dono finaliza no cartão,
 * e o 48h e o 72h são do relógio.
 */
export function BoardScreen() {
  const navigate = useNavigate();
  const invalidate = useInvalidateRetention();
  const [search, setSearch] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [transferring, setTransferring] = useState(false);

  const filters: RetentionFilters = useMemo(
    () => ({
      ...(search.trim() ? { q: search.trim() } : {}),
      ...(from ? { from } : {}),
      ...(to ? { to } : {}),
    }),
    [search, from, to],
  );
  const { ready, refused, errors, loading } = useRetentionSummaries(filters);

  /* A escala das bases, sem repetir a mesma pessoa — a ordem é a da primeira base. */
  const operators = useMemo(() => {
    const seen = new Map<string, string>();
    for (const { summary } of ready) {
      for (const o of summary.operators)
        if (!seen.has(o.userId)) seen.set(o.userId, o.name);
    }
    return [...seen].map(([id, name]) => ({ id, name }));
  }, [ready]);

  const counts = useMemo(() => {
    const total = new Map<string, number>();
    for (const { summary } of ready) {
      for (const c of summary.columns)
        total.set(c.key, (total.get(c.key) ?? 0) + c.count);
    }
    return total;
  }, [ready]);

  const stages: KanbanStage[] = useMemo(() => {
    const stage = (id: string, name: string, color: KanbanColor): KanbanStage => ({
      id,
      name,
      color,
    });
    return [
      stage('inicio', RETENTION_PHASE_LABELS.inicio, 'cinza'),
      ...operators.flatMap((o) => [
        stage(`operador:${o.id}:em_atendimento`, o.name, 'azul'),
        stage(`operador:${o.id}:atendimento_48h`, `${o.name} · 48h`, 'amarelo'),
      ]),
      stage('envio_email_72h', RETENTION_PHASE_LABELS.envio_email_72h, 'laranja'),
      stage('final:retido', RETENTION_PHASE_LABELS.retido, 'verde'),
      stage('final:cancelado', RETENTION_PHASE_LABELS.cancelado, 'vermelho'),
      stage(
        'final:divergencia_documentacao',
        RETENTION_PHASE_LABELS.divergencia_documentacao,
        'roxo',
      ),
    ];
  }, [operators]);

  /**
   * Uma página da coluna, das duas bases. O cursor é o de cada base, guardado
   * junto; a base que acabou não é pedida de novo.
   */
  const loadPage = useCallback(
    async (stageId: string, cursor: string | null): Promise<KanbanPage<BoardCard>> => {
      const state: Record<string, string | null | 'fim'> = cursor
        ? JSON.parse(cursor)
        : {};
      const pages = await Promise.all(
        ready.map(async ({ tenant }) => {
          const at = state[tenant.slug];
          if (at === 'fim') return { tenant, items: [], next: 'fim' as const };
          const page = ok(
            await api.retention.cards({
              params: { tenant: tenant.slug },
              query: {
                ...filters,
                column: stageId,
                limit: 30,
                ...(at ? { cursor: at } : {}),
              },
            }),
            200,
            `Não foi possível carregar a coluna da ${tenant.name}.`,
          );
          return { tenant, items: page.items, next: page.nextCursor ?? ('fim' as const) };
        }),
      );
      const final = parseRetentionColumnKey(stageId)?.kind === 'final';
      const items = pages
        .flatMap((p) => p.items.map((card) => ({ card, tenant: p.tenant })))
        .sort((a, b) =>
          final
            ? (b.card.completedAt ?? '').localeCompare(a.card.completedAt ?? '')
            : a.card.phaseSince.localeCompare(b.card.phaseSince),
        );
      const nextState = Object.fromEntries(pages.map((p) => [p.tenant.slug, p.next]));
      const more = pages.some((p) => p.next !== 'fim');
      return {
        items,
        next: more ? JSON.stringify(nextState) : null,
        total: counts.get(stageId) ?? 0,
      };
    },
    [ready, filters, counts],
  );

  const canDistribute = ready.some((r) => r.summary.can.distribute);

  return (
    <Page measure="cheia">
      <PageHeader
        title="Kanban Cancelamento"
        actions={
          canDistribute && operators.length > 1 ? (
            <Button variant="contorno" icon="mover" onClick={() => setTransferring(true)}>
              Transferir coluna
            </Button>
          ) : undefined
        }
      >
        Os pedidos de cancelamento das bases, com o relógio de 48 e 72 horas.
      </PageHeader>

      {errors.map((e) => (
        <Alert key={e} tone="erro">
          {e}
        </Alert>
      ))}
      {refused.length > 0 && (
        <Alert tone="informacao">
          Sem acesso ao Kanban Cancelamento em {refused.map((t) => t.name).join(' e ')}:
          os cartões dessas bases não aparecem aqui.
        </Alert>
      )}
      {!loading && ready.length > 0 && operators.length === 0 && (
        <Alert tone="alerta">
          Ninguém está na escala. Os pedidos ficam no Início até alguém da Configuração
          montar a escala do rodízio.
        </Alert>
      )}

      <Toolbar
        search={{
          value: search,
          onChange: setSearch,
          placeholder: 'Nome, CPF, placa ou protocolo',
          label: 'Pesquisar pedidos de cancelamento',
        }}
        toolbar={
          <div className="flex flex-wrap items-end gap-3">
            <DateField label="Entrada de" value={from} onChange={setFrom} />
            <DateField label="até" value={to} onChange={setTo} />
          </div>
        }
      />

      {ready.length > 0 && (
        <Kanban<BoardCard>
          label="Kanban Cancelamento"
          stages={stages}
          cardId={(c) => `${c.tenant.slug}:${c.card.id}`}
          cardLabel={(c) =>
            `${c.card.memberName ?? 'Associado'}, placa ${c.card.plate ?? 'sem placa'}, ${c.tenant.name}`
          }
          loadPage={loadPage}
          reloadKey={JSON.stringify([filters, ready.map((r) => r.summary.columns)])}
          renderCard={(c) => <CardBody item={c} />}
          onOpenCard={(c) =>
            navigate({
              to: '/retention/requests/$id',
              params: { id: String(c.card.id) },
              search: { tenant: c.tenant.slug },
            })
          }
          emptyColumnText="Nenhum pedido nesta coluna."
        />
      )}

      {transferring && (
        <TransferColumnModal
          operators={operators}
          summaries={ready.filter((r) => r.summary.can.distribute)}
          onClose={() => setTransferring(false)}
          onDone={() => {
            setTransferring(false);
            invalidate();
          }}
        />
      )}
    </Page>
  );
}

function CardBody({ item }: { item: BoardCard }) {
  const { card, tenant } = item;
  const ageMs = Date.now() - new Date(card.phaseSince).getTime();
  const active = card.completedAt === null;
  return (
    <KanbanCardBody
      title={card.memberName ?? 'Associado sem cadastro'}
      tags={
        <>
          <KanbanTag>{tenant.slug.toUpperCase()}</KanbanTag>
          <KanbanTag>{card.plate ?? 'Sem placa'}</KanbanTag>
          {card.labels.map((l) => (
            <KanbanTag key={l.id} color={l.color}>
              {l.name}
            </KanbanTag>
          ))}
        </>
      }
      detail={
        active ? (
          <KanbanTag
            tone={TONE_OF_AGE[retentionPhaseAgeTone(card.phaseSince, new Date())]}
          >
            Na fase {formatAge(ageMs)}
          </KanbanTag>
        ) : (
          `${card.protocol} · ${card.operator?.name ?? 'sem operador'}`
        )
      }
      alerts={
        card.fixedAmount ? (
          <KanbanTag>Valor fixo {moneyText(card.fixedAmount)}</KanbanTag>
        ) : undefined
      }
    />
  );
}

/** "Transferir coluna p/…" do legado — da escala de uma pessoa para outra. */
function TransferColumnModal({
  operators,
  summaries,
  onClose,
  onDone,
}: {
  operators: Array<{ id: string; name: string }>;
  summaries: Array<{ tenant: RetentionTenant; summary: RetentionSummary }>;
  onClose: () => void;
  onDone: () => void;
}) {
  const [fromId, setFromId] = useState('');
  const [phase, setPhase] = useState<'em_atendimento' | 'atendimento_48h'>(
    'em_atendimento',
  );
  const [toId, setToId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function run() {
    setBusy(true);
    setError(null);
    /*
     * Só as bases em que a coluna tem cartão: a pessoa de destino pode não
     * estar na escala de uma base em que a origem não tem nada, e chamar ali
     * recusaria depois de a outra base já ter transferido.
     */
    const key = `operador:${fromId}:${phase}`;
    const tenants = summaries
      .filter((s) => (s.summary.columns.find((c) => c.key === key)?.count ?? 0) > 0)
      .map((s) => s.tenant);
    if (tenants.length === 0) {
      setError('Essa coluna não tem cartão em nenhuma base.');
      setBusy(false);
      return;
    }
    try {
      for (const tenant of tenants) {
        ok(
          await api.retention.transferColumn({
            params: { tenant: tenant.slug },
            body: { fromOperatorId: fromId, phase, toOperatorId: toId },
          }),
          200,
          `Não foi possível transferir na ${tenant.name}.`,
        );
      }
      onDone();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Não foi possível transferir.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal open onOpenChange={(o) => !o && onClose()} title="Transferir coluna">
      <div className="grid gap-3">
        <Select label="De" value={fromId} onChange={(e) => setFromId(e.target.value)}>
          <option value="">Escolha</option>
          {operators.map((o) => (
            <option key={o.id} value={o.id}>
              {o.name}
            </option>
          ))}
        </Select>
        <Select
          label="Coluna"
          value={phase}
          onChange={(e) => setPhase(e.target.value as typeof phase)}
        >
          <option value="em_atendimento">Em atendimento</option>
          <option value="atendimento_48h">48 horas</option>
        </Select>
        <Select label="Para" value={toId} onChange={(e) => setToId(e.target.value)}>
          <option value="">Escolha</option>
          {operators
            .filter((o) => o.id !== fromId)
            .map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
        </Select>
        <p className="text-sm text-texto-suave">
          O relógio dos cartões não zera: eles continuam contando desde que entraram na
          fase.
        </p>
        {error && <Alert tone="erro">{error}</Alert>}
      </div>
      <ModalActions>
        <Button variant="contorno" onClick={onClose}>
          Cancelar
        </Button>
        <Button loading={busy} disabled={!fromId || !toId} onClick={run}>
          Transferir
        </Button>
      </ModalActions>
    </Modal>
  );
}
